import React, { useState, useEffect } from 'react';
import {
  Card,
  Typography,
  Table,
  Space,
  Button,
  Tag,
  Select,
  Input,
  Row,
  Col,
  message,
  Spin,
  Tooltip,
  Avatar,
  Badge,
  Modal
} from 'antd';
import {
  PlusOutlined,
  EyeOutlined,
  SearchOutlined,
  UserOutlined,
  MailOutlined,
  ClockCircleOutlined,
  CheckCircleOutlined,
  CloseCircleOutlined,
  ExclamationCircleOutlined,
  TeamOutlined,
  SettingOutlined
} from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import { userService } from '../../services';
import { formatDate, debounce } from '../../utils/helpers';
import dayjs from 'dayjs';

const { Title } = Typography;
const { Option } = Select;
const { Search } = Input;

const UserList = () => {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 20,
    total: 0,
    hasMore: true,
  });
  const [filters, setFilters] = useState({
    keyword: '',
    roleFilter: null,
    statusFilter: null,
  });
  const [availableFilters, setAvailableFilters] = useState({
    roles: ['admin', 'manager', 'stock', 'member', 'customer'],
    statuses: ['active', 'inactive', 'banned']
  });
  
  // Mobile responsive state
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);
  
  // Role change modal states
  const [roleChangeModalVisible, setRoleChangeModalVisible] = useState(false);
  const [selectedUserForRoleChange, setSelectedUserForRoleChange] = useState(null);
  const [roleChangeLoading, setRoleChangeLoading] = useState(false);
  const [currentUserRole, setCurrentUserRole] = useState(null);
  
  // Status change modal states
  const [statusChangeModalVisible, setStatusChangeModalVisible] = useState(false);
  const [selectedUserForStatusChange, setSelectedUserForStatusChange] = useState(null);
  const [statusChangeLoading, setStatusChangeLoading] = useState(false);
  const [currentUserStatus, setCurrentUserStatus] = useState(null);
  
  const navigate = useNavigate();

  // Handle window resize for mobile detection
  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth <= 768);
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Load users data
  const loadUsers = async (isLoadMore = false) => {
    try {
      if (isLoadMore) {
        setLoadingMore(true);
      } else {
        setLoading(true);
      }
      
      const offset = isLoadMore ? users.length : 0;
      const params = {
        ...filters,
        offset,
        limit: pagination.pageSize,
      };

      const response = await userService.getList(params);

      if (response.success) {
        const newUsers = response.data || [];
        
        if (isLoadMore) {
          setUsers(prev => [...prev, ...newUsers]);
        } else {
          setUsers(newUsers);
        }
        
        setPagination(prev => ({
          ...prev,
          total: response.pagination?.count || 0,
          hasMore: newUsers.length === pagination.pageSize,
        }));

        // Update available filters if provided
        if (response.availableFilters) {
          setAvailableFilters(response.availableFilters);
        }
      } else {
        message.error(response.message || 'Không thể tải danh sách người dùng');
      }
    } catch (error) {
      //console.error('Error loading users:', error);
      if (error.response?.status === 401) {
        message.error('Phiên đăng nhập đã hết hạn. Vui lòng đăng nhập lại.');
        navigate('/login');
      } else if (error.response?.status === 403) {
        message.error('Bạn không có quyền xem danh sách người dùng');
      } else {
        message.error('Có lỗi xảy ra khi tải danh sách người dùng');
      }
    } finally {
      setLoading(false);
      setLoadingMore(false);
    }
  };

  // Debounced search
  const handleSearch = debounce((value) => {
    setFilters(prev => ({ ...prev, keyword: value }));
    setPagination(prev => ({ ...prev, current: 1, hasMore: true }));
  }, 500);

  // Handle role filter change
  const handleRoleFilter = (value) => {
    setFilters(prev => ({ ...prev, roleFilter: value }));
    setPagination(prev => ({ ...prev, current: 1, hasMore: true }));
  };

  // Handle status filter change
  const handleStatusFilter = (value) => {
    setFilters(prev => ({ ...prev, statusFilter: value }));
    setPagination(prev => ({ ...prev, current: 1, hasMore: true }));
  };

  // Handle load more
  const handleLoadMore = () => {
    loadUsers(true);
  };

  // Handle view user details
  const handleViewUser = (user) => {
    navigate(`/users/view/${user.id}`);
  };

  // Handle role change
  const handleRoleChange = (user) => {
    setSelectedUserForRoleChange(user);
    setCurrentUserRole(user.role);
    setRoleChangeModalVisible(true);
  };

  // Handle role change submit
  const handleRoleChangeSubmit = async (newRole) => {
    if (!selectedUserForRoleChange || !newRole) return;

    try {
      setRoleChangeLoading(true);
      const response = await userService.updateRole(selectedUserForRoleChange.id, newRole);

      if (response.success) {
        message.success(`Đã thay đổi vai trò của ${selectedUserForRoleChange.full_name} thành ${getRoleDisplayName(newRole)}`);
        setRoleChangeModalVisible(false);
        setSelectedUserForRoleChange(null);
        setCurrentUserRole(null);
        loadUsers(); // Reload the list to show updated roles
      } else {
        message.error(response.message || 'Có lỗi xảy ra khi thay đổi vai trò');
      }
    } catch (error) {
      //console.error('Error updating user role:', error);
      if (error.response?.status === 403) {
        message.error('Bạn không có quyền thay đổi vai trò của người dùng này');
      } else if (error.response?.status === 401) {
        message.error('Phiên đăng nhập đã hết hạn. Vui lòng đăng nhập lại.');
        navigate('/login');
      } else {
        message.error('Có lỗi xảy ra khi thay đổi vai trò người dùng');
      }
    } finally {
      setRoleChangeLoading(false);
    }
  };

  // Handle role change cancel
  const handleRoleChangeCancel = () => {
    setRoleChangeModalVisible(false);
    setSelectedUserForRoleChange(null);
    setCurrentUserRole(null);
  };

  // Handle status change
  const handleStatusChange = (user) => {
    setSelectedUserForStatusChange(user);
    setCurrentUserStatus(user.status);
    setStatusChangeModalVisible(true);
  };

  // Handle status change submit
  const handleStatusChangeSubmit = async (newStatus) => {
    if (!selectedUserForStatusChange || !newStatus) return;

    try {
      setStatusChangeLoading(true);
      const response = await userService.updateStatus(selectedUserForStatusChange.id, newStatus);

      if (response.success) {
        const statusInfo = getStatusInfo(newStatus);
        message.success(`Đã thay đổi trạng thái của ${selectedUserForStatusChange.full_name} thành ${statusInfo.name}`);
        setStatusChangeModalVisible(false);
        setSelectedUserForStatusChange(null);
        setCurrentUserStatus(null);
        loadUsers(); // Reload the list to show updated status
      } else {
        message.error(response.message || 'Có lỗi xảy ra khi thay đổi trạng thái');
      }
    } catch (error) {
      //console.error('Error updating user status:', error);
      if (error.response?.status === 403) {
        message.error('Bạn không có quyền thay đổi trạng thái của người dùng này');
      } else if (error.response?.status === 401) {
        message.error('Phiên đăng nhập đã hết hạn. Vui lòng đăng nhập lại.');
        navigate('/login');
      } else {
        message.error('Có lỗi xảy ra khi thay đổi trạng thái người dùng');
      }
    } finally {
      setStatusChangeLoading(false);
    }
  };

  // Handle status change cancel
  const handleStatusChangeCancel = () => {
    setStatusChangeModalVisible(false);
    setSelectedUserForStatusChange(null);
    setCurrentUserStatus(null);
  };

  // Check if user can change role (permission check)
  const canChangeRole = (targetUser) => {
    // This is a frontend permission check - backend will do the final validation
    // For now, allow all role changes (backend will enforce permissions)
    return true;
  };

  // Get available roles for role change (based on current user permissions)
  const getAvailableRolesForChange = () => {
    // For now, return all roles (backend will filter based on permissions)
    return availableFilters.roles;
  };

  // Get role display name
  const getRoleDisplayName = (role) => {
    const roleNames = {
      'admin': 'Quản trị viên',
      'manager': 'Quản lý',
      'stock': 'Nhân viên kho',
      'member': 'Thành viên',
      'customer': 'Khách hàng'
    };
    return roleNames[role] || role;
  };

  // Get role color
  const getRoleColor = (role) => {
    const roleColors = {
      'admin': 'red',
      'manager': 'orange',
      'stock': 'blue',
      'member': 'green',
      'customer': 'purple'
    };
    return roleColors[role] || 'default';
  };

  // Get status display name and color
  const getStatusInfo = (status) => {
    const statusInfo = {
      'active': { name: 'Hoạt động', color: 'green', icon: <CheckCircleOutlined /> },
      'inactive': { name: 'Không hoạt động', color: 'orange', icon: <ExclamationCircleOutlined /> },
      'banned': { name: 'Bị cấm', color: 'red', icon: <CloseCircleOutlined /> }
    };
    return statusInfo[status] || { name: status, color: 'default', icon: <ClockCircleOutlined /> };
  };

  // Table columns
  const columns = [
    {
      title: 'Người dùng',
      key: 'user',
      width: 250,
      render: (_, record) => (
        <Space>
          <Avatar
            icon={<UserOutlined />}
            style={{ backgroundColor: '#1890ff' }}
          >
            {record.full_name?.charAt(0)?.toUpperCase()}
          </Avatar>
          <div>
            <div style={{ fontWeight: 'bold' }}>{record.full_name}</div>
            <div style={{ color: '#666', fontSize: '12px' }}>@{record.username}</div>
          </div>
        </Space>
      ),
    },
    {
      title: 'ID',
      dataIndex: 'id',
      key: 'id',
      width: 80,
      render: (id) => (
        <span style={{ 
          fontFamily: 'monospace', 
          fontSize: '12px', 
          color: '#666',
          backgroundColor: '#f5f5f5',
          padding: '2px 6px',
          borderRadius: '4px'
        }}>
          #{id}
        </span>
      ),
    },
    {
      title: 'Email',
      dataIndex: 'email',
      key: 'email',
      width: 200,
      render: (email) => (
        <Space>
          <MailOutlined style={{ color: '#1890ff' }} />
          <span>{email}</span>
        </Space>
      ),
    },
    {
      title: 'Vai trò',
      dataIndex: 'role',
      key: 'role',
      width: 120,
      render: (role) => (
        <Tag color={getRoleColor(role)}>
          {getRoleDisplayName(role)}
        </Tag>
      ),
    },
    {
      title: 'Trạng thái',
      dataIndex: 'status',
      key: 'status',
      width: 130,
      render: (status) => {
        const statusInfo = getStatusInfo(status);
        return (
          <Badge
            status={statusInfo.color === 'green' ? 'success' :
                   statusInfo.color === 'orange' ? 'warning' :
                   statusInfo.color === 'red' ? 'error' : 'default'}
            text={
              <Space>
                {statusInfo.icon}
                {statusInfo.name}
              </Space>
            }
          />
        );
      },
    },
    {
      title: 'Ngày tạo',
      dataIndex: 'created_at',
      key: 'created_at',
      width: 150,
      render: (date) => formatDate(date),
      sorter: true,
    },
    {
      title: 'Cập nhật cuối',
      dataIndex: 'updated_at',
      key: 'updated_at',
      width: 150,
      render: (date) => formatDate(date),
      sorter: true,
    },
    {
      title: 'Thao tác',
      key: 'actions',
      width: 150,
      render: (_, record) => (
        <Space size="small">
          <Tooltip title="Xem chi tiết">
            <Button
              type="text"
              icon={<EyeOutlined />}
              size="small"
              onClick={() => handleViewUser(record)}
            />
          </Tooltip>
          <Tooltip title="Thay đổi vai trò">
            <Button
              type="text"
              icon={<TeamOutlined />}
              size="small"
              style={{ color: '#1890ff' }}
              onClick={() => handleRoleChange(record)}
            />
          </Tooltip>
          <Tooltip title="Thay đổi trạng thái">
            <Button
              type="text"
              icon={<SettingOutlined />}
              size="small"
              style={{ color: '#fa8c16' }}
              onClick={() => handleStatusChange(record)}
            />
          </Tooltip>
        </Space>
      ),
    },
  ];

  // Load data on component mount and when filters change
  useEffect(() => {
    loadUsers();
  }, [filters]);

  // Mobile card rendering function
  const renderMobileCard = (user) => (
    <Card
      key={user.id}
      size="small"
      style={{ marginBottom: 12 }}
      actions={[
        <Tooltip title="Xem chi tiết">
          <Button
            type="text"
            icon={<EyeOutlined />}
            onClick={() => handleViewUser(user)}
          />
        </Tooltip>,
        <Tooltip title="Thay đổi vai trò">
          <Button
            type="text"
            icon={<TeamOutlined />}
            style={{ color: '#1890ff' }}
            onClick={() => handleRoleChange(user)}
          />
        </Tooltip>,
        <Tooltip title="Thay đổi trạng thái">
          <Button
            type="text"
            icon={<SettingOutlined />}
            style={{ color: '#fa8c16' }}
            onClick={() => handleStatusChange(user)}
          />
        </Tooltip>
      ]}
    >
      <div style={{ display: 'flex', alignItems: 'center', marginBottom: 12 }}>
        <Avatar
          icon={<UserOutlined />}
          style={{ backgroundColor: '#1890ff', marginRight: 12 }}
        >
          {user.full_name?.charAt(0)?.toUpperCase()}
        </Avatar>
        <div style={{ flex: 1 }}>
          <div style={{ fontWeight: 'bold', fontSize: '16px' }}>{user.full_name}</div>
          <div style={{ color: '#666', fontSize: '12px' }}>@{user.username}</div>
        </div>
        <span style={{ 
          fontFamily: 'monospace', 
          fontSize: '11px', 
          color: '#666',
          backgroundColor: '#f5f5f5',
          padding: '2px 6px',
          borderRadius: '4px'
        }}>
          #{user.id}
        </span>
      </div>
      
      <div style={{ marginBottom: 8 }}>
        <MailOutlined style={{ color: '#1890ff', marginRight: 8 }} />
        <span style={{ fontSize: '14px' }}>{user.email}</span>
      </div>
      
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
        <div>
          <span style={{ marginRight: 8, fontSize: '12px', color: '#666' }}>Vai trò:</span>
          <Tag color={getRoleColor(user.role)} size="small">
            {getRoleDisplayName(user.role)}
          </Tag>
        </div>
        <div>
          <span style={{ marginRight: 8, fontSize: '12px', color: '#666' }}>Trạng thái:</span>
          <Badge
            status={getStatusInfo(user.status).color === 'green' ? 'success' :
                   getStatusInfo(user.status).color === 'orange' ? 'warning' :
                   getStatusInfo(user.status).color === 'red' ? 'error' : 'default'}
            text={
              <span style={{ fontSize: '12px' }}>
                {getStatusInfo(user.status).name}
              </span>
            }
          />
        </div>
      </div>
      
      <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '11px', color: '#999' }}>
        <span>Tạo: {formatDate(user.created_at)}</span>
        <span>Cập nhật: {formatDate(user.updated_at)}</span>
      </div>
    </Card>
  );

  return (
    <div style={{ padding: isMobile ? 8 : 24 }}>
      <Card>
        <div style={{ 
          marginBottom: 16, 
          display: 'flex', 
          justifyContent: 'space-between', 
          alignItems: 'center',
          flexDirection: isMobile ? 'column' : 'row',
          gap: isMobile ? 12 : 0
        }}>
          <Title level={isMobile ? 4 : 3} style={{ margin: 0 }}>
            <UserOutlined style={{ marginRight: 8 }} />
            Danh sách người dùng
          </Title>
          <Button
            type="primary"
            icon={<PlusOutlined />}
            onClick={() => navigate('/users/create')}
            size={isMobile ? 'middle' : 'default'}
            block={isMobile}
          >
            Thêm người dùng
          </Button>
        </div>

        {/* Filters */}
        <Row gutter={isMobile ? [0, 12] : 16} style={{ marginBottom: 16 }}>
          <Col xs={24} sm={12} md={8} lg={6}>
            <Search
              placeholder="Tìm theo username, tên, email..."
              allowClear
              onSearch={handleSearch}
              onChange={(e) => handleSearch(e.target.value)}
              prefix={<SearchOutlined />}
              size={isMobile ? 'middle' : 'default'}
            />
          </Col>
          <Col xs={24} sm={12} md={8} lg={6}>
            <Select
              placeholder="Lọc theo vai trò"
              allowClear
              style={{ width: '100%' }}
              onChange={handleRoleFilter}
              value={filters.roleFilter}
              size={isMobile ? 'middle' : 'default'}
            >
              {availableFilters.roles.map(role => (
                <Option key={role} value={role}>
                  {getRoleDisplayName(role)}
                </Option>
              ))}
            </Select>
          </Col>
          <Col xs={24} sm={12} md={8} lg={6}>
            <Select
              placeholder="Lọc theo trạng thái"
              allowClear
              style={{ width: '100%' }}
              onChange={handleStatusFilter}
              value={filters.statusFilter}
              size={isMobile ? 'middle' : 'default'}
            >
              {availableFilters.statuses.map(status => {
                const statusInfo = getStatusInfo(status);
                return (
                  <Option key={status} value={status}>
                    <Space>
                      {statusInfo.icon}
                      {statusInfo.name}
                    </Space>
                  </Option>
                );
              })}
            </Select>
          </Col>
        </Row>

        {/* Users Table or Mobile Cards */}
        {isMobile ? (
          <div>
            {loading ? (
              <div style={{ textAlign: 'center', padding: '40px' }}>
                <Spin size="large" />
                <div style={{ marginTop: 16, color: '#666' }}>Đang tải...</div>
              </div>
            ) : users.length > 0 ? (
              <>
                {users.map(user => renderMobileCard(user))}
                {/* Load More Button */}
                {pagination.hasMore && (
                  <div style={{ textAlign: 'center', marginTop: 16 }}>
                    <Button
                      type="primary"
                      loading={loadingMore}
                      onClick={handleLoadMore}
                      size="large"
                      style={{ minWidth: '200px' }}
                    >
                      {loadingMore ? 'Đang tải...' : `Xem thêm (${users.length}/${pagination.total})`}
                    </Button>
                  </div>
                )}
              </>
            ) : (
              <div style={{ textAlign: 'center', padding: '40px', color: '#666' }}>
                <UserOutlined style={{ fontSize: '48px', color: '#d9d9d9', marginBottom: 16 }} />
                <div>Không tìm thấy người dùng nào</div>
                <div style={{ fontSize: '14px', marginTop: 8 }}>
                  Thử điều chỉnh bộ lọc hoặc thêm người dùng mới
                </div>
              </div>
            )}
          </div>
        ) : (
          <>
            <Table
              columns={columns}
              dataSource={users}
              rowKey="id"
              loading={loading}
              pagination={false}
              scroll={{ x: 1400 }}
              size="middle"
            />
            {/* Desktop Load More */}
            {pagination.hasMore && (
              <div style={{ textAlign: 'center', marginTop: 24 }}>
                <Button
                  type="primary"
                  loading={loadingMore}
                  onClick={handleLoadMore}
                  size="large"
                  style={{ minWidth: '200px' }}
                >
                  {loadingMore ? 'Đang tải...' : `Xem thêm (${users.length}/${pagination.total})`}
                </Button>
              </div>
            )}
          </>
        )}

        {/* Summary */}
        {!loading && users.length > 0 && (
          <div style={{ 
            marginTop: 16, 
            textAlign: 'center', 
            color: '#666',
            padding: isMobile ? '12px' : '16px',
            backgroundColor: '#f5f5f5',
            borderRadius: '6px'
          }}>
            Đang hiển thị: {users.length} / {pagination.total} người dùng
          </div>
        )}

        {/* Empty state - removed duplicate */}
      </Card>

      {/* Role Change Modal */}
      <Modal
        title="Thay đổi vai trò người dùng"
        open={roleChangeModalVisible}
        onCancel={handleRoleChangeCancel}
        footer={null}
        width={isMobile ? '90%' : 500}
        style={isMobile ? { top: 20 } : {}}
      >
        {selectedUserForRoleChange && (
          <div>
            <div style={{ marginBottom: 16 }}>
              <strong>Người dùng:</strong> {selectedUserForRoleChange.full_name}
            </div>
            <div style={{ marginBottom: 16 }}>
              <strong>Vai trò hiện tại:</strong>{' '}
              <Tag color={getRoleColor(selectedUserForRoleChange.role)}>
                {getRoleDisplayName(selectedUserForRoleChange.role)}
              </Tag>
            </div>
            <div style={{ marginBottom: 24 }}>
              <strong>Chọn vai trò mới:</strong>
            </div>
            <div style={isMobile ? { display: 'flex', flexDirection: 'column', gap: '12px' } : {}}>
              {isMobile ? (
                // Mobile: Each role as full-width button
                getAvailableRolesForChange().map(role => (
                  <Button
                    key={role}
                    type={selectedUserForRoleChange.role === role ? 'default' : 'primary'}
                    block
                    loading={roleChangeLoading}
                    onClick={() => handleRoleChangeSubmit(role)}
                    disabled={selectedUserForRoleChange.role === role}
                    size="large"
                    style={{
                      height: '60px',
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                      padding: '0 16px',
                      fontSize: '16px'
                    }}
                  >
                    <span style={{ fontWeight: 'bold' }}>
                      {getRoleDisplayName(role)}
                    </span>
                    <Tag color={getRoleColor(role)}>
                      {role.toUpperCase()}
                    </Tag>
                  </Button>
                ))
              ) : (
                // Desktop: Grid layout
                <Row gutter={[16, 16]}>
                  {getAvailableRolesForChange().map(role => (
                    <Col span={12} key={role}>
                      <Button
                        type={currentUserRole === role ? 'primary' : 'default'}
                        block
                        loading={roleChangeLoading}
                        onClick={() => handleRoleChangeSubmit(role)}
                        disabled={selectedUserForRoleChange.role === role}
                        style={{
                          height: 'auto',
                          padding: '12px',
                          display: 'flex',
                          flexDirection: 'column',
                          alignItems: 'center',
                          gap: '8px'
                        }}
                      >
                        <div style={{ fontWeight: 'bold' }}>
                          {getRoleDisplayName(role)}
                        </div>
                        <Tag color={getRoleColor(role)} size="small">
                          {role.toUpperCase()}
                        </Tag>
                      </Button>
                    </Col>
                  ))}
                </Row>
              )}
            </div>
            <div style={{ marginTop: 24, textAlign: 'center' }}>
              <Button onClick={handleRoleChangeCancel}>
                Hủy
              </Button>
            </div>
          </div>
        )}
      </Modal>

      {/* Status Change Modal */}
      <Modal
        title="Thay đổi trạng thái người dùng"
        open={statusChangeModalVisible}
        onCancel={handleStatusChangeCancel}
        footer={null}
        width={isMobile ? '90%' : 500}
        style={isMobile ? { top: 20 } : {}}
      >
        {selectedUserForStatusChange && (
          <div>
            <div style={{ marginBottom: 16 }}>
              <strong>Người dùng:</strong> {selectedUserForStatusChange.full_name}
            </div>
            <div style={{ marginBottom: 16 }}>
              <strong>Trạng thái hiện tại:</strong>{' '}
              <Badge
                status={getStatusInfo(selectedUserForStatusChange.status).color === 'green' ? 'success' :
                       getStatusInfo(selectedUserForStatusChange.status).color === 'orange' ? 'warning' :
                       getStatusInfo(selectedUserForStatusChange.status).color === 'red' ? 'error' : 'default'}
                text={
                  <Space>
                    {getStatusInfo(selectedUserForStatusChange.status).icon}
                    {getStatusInfo(selectedUserForStatusChange.status).name}
                  </Space>
                }
              />
            </div>
            <div style={{ marginBottom: 24 }}>
              <strong>Chọn trạng thái mới:</strong>
            </div>
            <div style={isMobile ? { display: 'flex', flexDirection: 'column', gap: '12px' } : {}}>
              {isMobile ? (
                // Mobile: Each status as full-width button
                availableFilters.statuses.map(status => {
                  const statusInfo = getStatusInfo(status);
                  return (
                    <Button
                      key={status}
                      type={selectedUserForStatusChange.status === status ? 'default' : 'primary'}
                      block
                      loading={statusChangeLoading}
                      onClick={() => handleStatusChangeSubmit(status)}
                      disabled={selectedUserForStatusChange.status === status}
                      size="large"
                      style={{
                        height: '60px',
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        padding: '0 16px',
                        fontSize: '16px'
                      }}
                    >
                      <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                        {statusInfo.icon}
                        <span style={{ fontWeight: 'bold' }}>
                          {statusInfo.name}
                        </span>
                      </div>
                      <Tag color={statusInfo.color}>
                        {status.toUpperCase()}
                      </Tag>
                    </Button>
                  );
                })
              ) : (
                // Desktop: Grid layout
                <Row gutter={[16, 16]}>
                  {availableFilters.statuses.map(status => {
                    const statusInfo = getStatusInfo(status);
                    return (
                      <Col span={12} key={status}>
                        <Button
                          type={currentUserStatus === status ? 'primary' : 'default'}
                          block
                          loading={statusChangeLoading}
                          onClick={() => handleStatusChangeSubmit(status)}
                          disabled={selectedUserForStatusChange.status === status}
                          style={{
                            height: 'auto',
                            padding: '12px',
                            display: 'flex',
                            flexDirection: 'column',
                            alignItems: 'center',
                            gap: '8px'
                          }}
                        >
                          <div style={{ fontWeight: 'bold' }}>
                            {statusInfo.name}
                          </div>
                          <Badge
                            status={statusInfo.color === 'green' ? 'success' :
                                   statusInfo.color === 'orange' ? 'warning' :
                                   statusInfo.color === 'red' ? 'error' : 'default'}
                            text={
                              <Space>
                                {statusInfo.icon}
                                {status.toUpperCase()}
                              </Space>
                            }
                          />
                        </Button>
                      </Col>
                    );
                  })}
                </Row>
              )}
            </div>
            <div style={{ marginTop: 24, textAlign: 'center' }}>
              <Button onClick={handleStatusChangeCancel}>
                Hủy
              </Button>
            </div>
          </div>
        )}
      </Modal>
    </div>
  );
};

export default UserList;
